/* eslint-disable react/prop-types */
/**
 * ScheduleNextDialog — "Request Next Appointment Scheduling" picker shown
 * after NextStepsDialog. Extracted from the inline dialog in App.js
 * (~line 56781) for the V2 note host (Jun 2026 V2 action parity).
 */
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DebouncedTextarea } from '../../ui/debounced-textarea';

export const ScheduleNextDialog = ({ ctx }) => {
  const {
    API, getSchedulersForProvider, onSent, scheduleNextData, selectedClient,
    selectedNote, setScheduleNextData, setShowScheduleNextDialog,
    showScheduleNextDialog, user,
  } = ctx;

  return (
    <Dialog open={showScheduleNextDialog} onOpenChange={setShowScheduleNextDialog}>
      <DialogContent className="max-w-md" data-testid="schedule-next-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-purple-600" />
            Schedule Next Appointment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Send a scheduling request to your scheduler for <strong>{selectedNote?.client_name || (selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'this client')}</strong>.
          </p>

          <div>
            <Label>Schedule in</Label>
            <Select
              value={scheduleNextData.timeframe}
              onValueChange={(value) => setScheduleNextData((prev) => ({ ...prev, timeframe: value }))}
            >
              <SelectTrigger className="mt-1" data-testid="schedule-next-timeframe-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1_week">1 Week</SelectItem>
                <SelectItem value="2_weeks">2 Weeks</SelectItem>
                <SelectItem value="3_weeks">3 Weeks</SelectItem>
                <SelectItem value="1_month">1 Month</SelectItem>
                <SelectItem value="6_weeks">6 Weeks</SelectItem>
                <SelectItem value="2_months">2 Months</SelectItem>
                <SelectItem value="3_months">3 Months</SelectItem>
                <SelectItem value="prn">PRN (As Needed)</SelectItem>
                <SelectItem value="custom">Custom Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scheduleNextData.timeframe === 'custom' && (
            <div>
              <Label>Target Date</Label>
              <Input
                type="date"
                value={scheduleNextData.customDate}
                onChange={(e) => setScheduleNextData((prev) => ({ ...prev, customDate: e.target.value }))}
                className="mt-1"
                data-testid="schedule-next-custom-date"
              />
            </div>
          )}

          <div>
            <Label>Notes for Scheduler (optional)</Label>
            <DebouncedTextarea
              value={scheduleNextData.notes}
              onChange={(e) => setScheduleNextData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="e.g., Client prefers morning appointments, follow up on medication adjustment..."
              className="mt-1"
              rows={3}
              data-testid="schedule-next-notes"
            />
          </div>

          {(() => {
            const schedulers = getSchedulersForProvider(user.id);
            return schedulers.length > 0 ? (
              <div className="bg-purple-50 p-3 rounded-lg text-sm">
                <span className="text-purple-700">
                  📤 This request will be sent to: <strong>{schedulers.map((s) => s.full_name || `${s.first_name} ${s.last_name}`).join(', ')}</strong>
                </span>
              </div>
            ) : null;
          })()}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowScheduleNextDialog(false)}
              data-testid="schedule-next-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="schedule-next-send-btn"
              onClick={async () => {
                try {
                  const schedulers = getSchedulersForProvider(user.id);
                  if (schedulers.length === 0) {
                    toast.error('No scheduler assigned to you');
                    return;
                  }
                  const clientName = selectedNote?.client_name ||
                    (selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client');
                  const clientId = selectedNote?.client_id || selectedClient?.id;

                  let targetDate = '';
                  const today = new Date();
                  const timeframeMap = {
                    '1_week': 7, '2_weeks': 14, '3_weeks': 21, '1_month': 30,
                    '6_weeks': 42, '2_months': 60, '3_months': 90,
                  };
                  if (scheduleNextData.timeframe === 'custom') {
                    targetDate = scheduleNextData.customDate;
                  } else if (scheduleNextData.timeframe === 'prn') {
                    targetDate = 'PRN';
                  } else {
                    const days = timeframeMap[scheduleNextData.timeframe] || 14;
                    const target = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
                    targetDate = target.toISOString().split('T')[0];
                  }

                  const timeframeLabel = {
                    '1_week': '1 week', '2_weeks': '2 weeks', '3_weeks': '3 weeks',
                    '1_month': '1 month', '6_weeks': '6 weeks', '2_months': '2 months',
                    '3_months': '3 months', prn: 'PRN (As Needed)',
                    custom: `by ${scheduleNextData.customDate}`,
                  }[scheduleNextData.timeframe];

                  for (const scheduler of schedulers) {
                    await axios.post(`${API}/tasks`, {
                      title: `Schedule appointment for ${clientName}`,
                      description: `**Scheduling Request from ${user.full_name || user.first_name}**\n\n` +
                        `Client: ${clientName}\n` +
                        `Schedule in: ${timeframeLabel}\n` +
                        `Target date: ${targetDate === 'PRN' ? 'As needed' : targetDate}\n` +
                        `${scheduleNextData.notes ? `\nNotes: ${scheduleNextData.notes}` : ''}`,
                      priority: 'medium',
                      status: 'pending',
                      assigned_to: scheduler.id,
                      created_by: user.id,
                      client_id: clientId,
                      task_type: 'scheduling_request',
                      due_date: targetDate === 'PRN' ? null : targetDate,
                    });
                  }

                  toast.success(`Scheduling request sent to ${schedulers.length > 1 ? 'schedulers' : schedulers[0].full_name || 'scheduler'}`);
                  setShowScheduleNextDialog(false);
                  onSent?.();
                } catch (error) {
                  console.error('Error creating scheduling task:', error);
                  toast.error('Failed to send scheduling request');
                }
              }}
            >
              Send Request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleNextDialog;
